import React, { useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Iconify, NextImage } from '@sdf/base';
import { Styles } from '@t2c/shared-fe';
import CloseIcon from '@mui/icons-material/Close';
import { FileUploader } from 'react-drag-drop-files';
import { useSnackbar } from 'notistack';
import { generatePreSignedUrl } from 'apps/t2c-fe/services/upload/generatePreSignedUrl';
import { usePresignedUrlUpload, useUploadId } from 'apps/t2c-fe/services';
import { extractFileMetadata } from 'apps/t2c-fe/services/upload/extractFileMetadata';
import ImageEditor from '../common/ImageEditor';
import { AspectRatios } from '@t2c/ai-video-editor';
import { resizeImageFile } from '../common/ResizeFunction';

interface UploadImageProps {
  uploadURL: string;
  openBrowseBRollModal: () => void;
  handleUpdateImage: (id: string, url: string) => void;
  abortController: React.MutableRefObject<any>;
  uploading: boolean;
  setUploading: React.Dispatch<React.SetStateAction<boolean>>;
  limit: number;
  preValidator?: (file: File) => Promise<void>;
  preImageValidator?: (file: Blob) => Promise<void>;
  orientation?: AspectRatios;
  resolution?: string;
  imageFile?: string;
  setImageFile?: React.Dispatch<React.SetStateAction<string>>;
  errorMsg?: string;
  setErrorMsg?: React.Dispatch<React.SetStateAction<string>>;
  setIsImage?: React.Dispatch<React.SetStateAction<boolean>>;
  uploadBackgroundColor?: string;
}

export const UploadImage: React.FC<UploadImageProps> = ({
  uploadURL,
  openBrowseBRollModal,
  handleUpdateImage,
  abortController,
  uploading,
  setUploading,
  limit,
  preValidator,
  preImageValidator,
  orientation,
  resolution,
  imageFile,
  setImageFile,
  errorMsg,
  setErrorMsg,
  setIsImage,
  uploadBackgroundColor,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync: uploadToPresignedUrl } = usePresignedUrlUpload({});
  const { mutateAsync: fetchUploadId } = useUploadId();
  const [showEditor, setShowEditor] = useState(false);

  const handleFileSelect = async (file) => {
    if (setIsImage) {
      setIsImage(true);
    }
    if (file.size > limit * 1048576) {
      const filePreview = URL.createObjectURL(file);
      file = await resizeImageFile(filePreview, {
        maxWidth: 2048,
        maxHeight: 2048,
        targetMaxBytes: limit * 1048576,
      });
    }

    // Run pre-validator if provided
    if (preValidator) {
      try {
        await preValidator(file);
      } catch (error) {
        const filePreview = URL.createObjectURL(file);
        setShowEditor(true);
        setImageFile(filePreview);
        return;
      }
    }

    // Run pre-image-validator if provided
    if (preImageValidator) {
      try {
        await preImageValidator(file);
      } catch (error) {
        enqueueSnackbar(error.message, { variant: 'error' });
        return;
      }
    }

    const filePreview = URL.createObjectURL(file);
    handleUpdateImage('', filePreview);
    handleFileUpload(file, filePreview);
  };

  const handleFileUpload = async (file, url) => {
    setUploading(true);
    try {
      abortController.current = new AbortController();
      const { signal } = abortController.current;

      const {
        uploadId: multiPartUploadId,
        preSignedUrls,
        format,
      } = await generatePreSignedUrl(file);
      await uploadToPresignedUrl({
        uploadId: multiPartUploadId,
        presignedUrls: preSignedUrls,
        file: file,
        signal: signal,
      });

      // Extract metadata from the file
      const metadata = await extractFileMetadata(file, format);

      let uploadRes;

      try {
        uploadRes = await fetchUploadId({
          url: preSignedUrls[0].url,
          format,
          mimeType: file.type,
          signal,
          metadata,
        });
      } catch (error) {
        console.error('Error fetching upload ID:', error);
        return;
      }
      handleUpdateImage(uploadRes?.uploadId, url);
      if (showEditor) {
        setShowEditor(false);
        setImageFile('');
      }
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError' || err.message === 'canceled') {
        console.log('Upload aborted');
        return;
      } else {
        console.error(err);
        throw err;
      }
    } finally {
      setUploading(false);
      abortController.current = null;
    }
  };

  const supportedFormats = ['jpg', 'jpeg', 'png'];

  return (
    <>
      <Box mt={1}>
        {!uploadURL && !uploading ? (
          <Box
            sx={{
              ...styles.uploader,
              backgroundColor: uploadBackgroundColor && uploadBackgroundColor,
            }}
          >
            <FileUploader
              multiple={false}
              handleChange={(file) => handleFileSelect(file)}
              name="file"
              types={supportedFormats}
              onTypeError={(err) => {
                enqueueSnackbar('Unsupported file type. Please upload supported file format', {
                  variant: 'error',
                });
              }}
              disabled={uploading}
            >
              <Box sx={styles.uploadSection}>
                <Iconify icon="icon-park-outline:upload-one" width="20px" height="20px" />
                <Typography sx={styles.uploaderText}>Upload</Typography>
              </Box>
            </FileUploader>
            <Box onClick={openBrowseBRollModal} sx={styles.browseMedia}>
              <Typography sx={styles.selecterText}>My Media</Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={styles.uploading}>
            {uploadURL ? (
              <Box sx={{ borderRadius: '10px' }}>
                <NextImage
                  src={uploadURL}
                  alt="UploadedImage"
                  style={{
                    objectFit: 'contain',
                    margin: 'auto',
                  }}
                  width={234}
                  height={100}
                />
              </Box>
            ) : (
              <Box sx={styles.uploadingPlaceholder}>
                <Typography sx={styles.uploaderText}>Uploading...</Typography>
              </Box>
            )}
            {uploading && (
              <CircularProgress
                size={25}
                sx={{
                  position: 'absolute',
                  left: '40%',
                  top: '40%',
                }}
              />
            )}
            {!uploading && (
              <CloseIcon
                fontSize={'small'}
                color="primary"
                sx={{
                  position: 'absolute',
                  right: '-15px',
                  top: '-10px',
                  cursor: 'pointer',
                  backgroundColor: (theme) => theme.palette.background.primary,
                  borderRadius: '50%',
                }}
                onClick={() => {
                  handleUpdateImage('', '');
                }}
              />
            )}
          </Box>
        )}
      </Box>
      {showEditor && (
        <ImageEditor
          imageFile={imageFile}
          handleFileSelect={handleFileSelect}
          orientation={orientation}
          resolution={resolution}
          open={showEditor}
          onClose={() => {
            setShowEditor(false);
            setErrorMsg('');
          }}
          errorMsg={errorMsg}
        />
      )}
    </>
  );
};

const styles: Styles = {
  uploader: {
    height: 100,
    width: 100,
    display: 'flex',
    borderWidth: 1,
    textAlign: 'center',
    overflow: 'hidden',
    borderStyle: 'solid',
    borderRadius: '10px',
    flexDirection: 'column',
    borderColor: (theme) => theme.palette.background.border,
  },
  uploadSection: {
    height: 65,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
  uploaderText: {
    color: (theme) => theme.palette.text.primary,
    fontSize: 15,
    fontWeight: 500,
  },
  browseMedia: {
    height: 35,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    backgroundColor: '#282130',
  },
  selecterText: {
    fontSize: 12,
    fontWeight: 400,
    textDecoration: 'underline',
    color: (theme) => theme.palette.text.coloredText,
  },
  uploading: {
    position: 'relative',
    borderRadius: '10px',
    minHeight: '100px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  uploadingPlaceholder: {
    width: '100%',
    minHeight: '100px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '10px',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: (theme) => theme.palette.background.border,
    backgroundColor: (theme) => theme.palette.background.secondary,
  },
};
